import logger from "@utils/logger";
import { clearSelectedClinic } from "@utils/clinicScopeStorage";
const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL;
export const login = async (
  data,
  setIsAuthenticated,
  setErrorMessage,
  navigate
) => {
  try {
    const response = await fetch(`${backendUrl}/auth/signin`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const responseData = await response.json();
      const token = responseData.data.access_token;
      const refresh_token = responseData.data.refresh_token;
      const expires_at = responseData.data.expires_at;
      const is_voice = responseData.data.is_voice_notes_user;
      const is_owner = responseData.data.is_owner;
      const role = responseData.data.role; // can be null
      const permission_preset = responseData.data.permission_preset; // can be null
      const is_admin = responseData.data.is_admin;
      const outbound_calls_enabled = responseData.data.outbound_calls_enabled;
      const requiresReset = responseData.data.requires_password_reset;
      const resetToken = responseData.data.reset_token;
      localStorage.setItem("is_admin", is_admin ? "true" : "false");
      localStorage.setItem("permission_preset", permission_preset ?? "");
      localStorage.setItem("is_owner", is_owner ? "true" : "false");
      localStorage.setItem("role", role ?? "");
      localStorage.setItem("email", data.email);
      localStorage.setItem("is_voice", is_voice);
      localStorage.setItem("has_scheduling", responseData.data.has_scheduling ? "true" : "false");
      localStorage.setItem("outbound_calls_enabled", outbound_calls_enabled ? "true" : "false");
      localStorage.setItem("transfer_campaigns_enabled",
        responseData.data.transfer_campaigns_enabled ? "true" : "false");
      logger.info(`User ${data.email} logged in successfully.`);
      if (requiresReset) {
        navigate(`/reset-password?token=${encodeURIComponent(resetToken)}`);
      } else {
        localStorage.setItem("access_token", token);
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("expires_at", expires_at);
        setIsAuthenticated(true);
        navigate("/");
      }
    } else {
      const errorData = await response.json();
      setErrorMessage(errorData.details);
      logger.warn(`Login failed for ${data.email}: ${errorData.details}`);
    }
  } catch (error) {
    setErrorMessage("An error occurred. Try again.");
    logger.error(`Signin error: ${error.message}`);
  }
};
export const signup = async (data, setErrorMessage) => {
  setErrorMessage("");
  try {
    const response = await fetch(`${backendUrl}/auth/signup`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logger.info(
        `User ${data.email} Verification code sent. Please check your email .`
      );
      return "success";
    } else {
      const errorData = await response.json();
      setErrorMessage(errorData.details);
      logger.warn(`Signup failed for ${data.email}: ${errorData.details}`);
    }
  } catch (error) {
    setErrorMessage("An error occurred. Try again.");
    logger.error(`Signup error: ${error.message}`);
  }
};
export const verifyEmail = async (
  data,
  setIsAuthenticated,
  setErrorMessage,
  navigate
) => {
  try {
    const response = await fetch(`${backendUrl}/auth/verify_email`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const responseData = await response.json();
      setErrorMessage(""); // Clear any previous errors
      const token = responseData.access_token;
      const refresh_token = responseData.refresh_token;
      const expires_at = responseData.expires_at;
      localStorage.setItem("access_token", token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_at", expires_at);
      localStorage.setItem("email", data.email);
      logger.info(`User ${data.email} registered successfully.`);
      setIsAuthenticated(true);
      navigate("/"); // Redirect after successful signup
    } else {
      const errorData = await response.json();
      setErrorMessage(errorData.details);
      logger.warn(`Signup failed for ${data.email}: ${errorData.details}`);
    }
  } catch (error) {
    setErrorMessage("An error occurred. Try again.");
    logger.error(`Signup error: ${error.message}`);
  }
};
export const resendCode = async (data, setErrorMessage) => {
  try {
    const response = await fetch(`${backendUrl}/auth/resend_code`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    if (response.ok) {
      setErrorMessage("New verification code sent. Check your email.");
    } else {
      setErrorMessage(result.details);
    }
  } catch (error) {
    setErrorMessage("An error occurred. Try again.");
    logger.error(`resend_code error: ${error.message}`);
  }
};
export const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      logger.error("No refresh token found. Logging out.");
      logoutUser(); // Call the logout function
      return null;
    }
    const response = await fetch(`${backendUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("Refresh token expired. Logging out.");
        logoutUser();
      }
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    localStorage.setItem("access_token", data.data.access_token);
    localStorage.setItem("refresh_token", data.data.refresh_token);
    localStorage.setItem("expires_at", data.data.expires_at);
    return data.data.access_token;
  } catch (error) {
    console.error("Token refresh failed:", error);
    throw error;
  }
};

export const logoutUser = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expires_at");
  localStorage.removeItem("email");
  localStorage.removeItem("is_owner");
  localStorage.removeItem("is_admin");
  localStorage.removeItem("role");
  localStorage.removeItem("permission_preset");
  localStorage.removeItem("is_voice");
  localStorage.removeItem("outbound_calls_enabled");
  localStorage.removeItem("transfer_campaigns_enabled");
  clearSelectedClinic();

  // Clean up impersonation state
  localStorage.removeItem("impersonating");
  localStorage.removeItem("impersonating_email");
  const adminKeys = [
    "admin_access_token",
    "admin_refresh_token",
    "admin_expires_at",
    "admin_email",
    "admin_is_owner",
    "admin_is_admin",
    "admin_role",
    "admin_permission_preset",
    "admin_is_voice",
  ];
  adminKeys.forEach((key) => localStorage.removeItem(key));

  window.location.href = "/";
};
export const forgotPassword = async (
  email, // { email }
  setErrorMessage,
  setSuccessMessage
) => {
  try {
    const response = await fetch(`${backendUrl}/auth/forgot-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const result = await response.json().catch(() => ({}));

    if (response.ok) {
      if (setErrorMessage) setErrorMessage("");
      if (setSuccessMessage) {
        setSuccessMessage(
          result.message ||
            "If an account with that email exists, a password reset link has been sent."
        );
      }
      logger.info(
        `Forgot password requested for ${email}. Reset email sent if account exists.`
      );
    } else {
      if (setSuccessMessage) setSuccessMessage("");
      if (setErrorMessage) {
        setErrorMessage(
          result.details ||
            result.message ||
            "Unable to process password reset request."
        );
      }
      logger.warn(
        `Forgot password failed for ${email}: ${
          result.details || result.message
        }`
      );
    }
  } catch (error) {
    if (setSuccessMessage) setSuccessMessage("");
    if (setErrorMessage) setErrorMessage("An error occurred. Try again.");
    logger.error(`Forgot password error: ${error.message}`);
  }
};

export const verifyResetToken = async ({ token }) => {
  try {
    const res = await fetch(`${backendUrl}/auth/reset-password/verify`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const error = new Error(
        data.details || data.message || "Token verification failed"
      );
      error.response = { data };
      error.status = res.status;
      throw error;
    }

    return data;
  } catch (error) {
    // Ensure consistent error shape
    if (!error.response) {
      error.response = { data: { message: error.message } };
    }
    throw error;
  }
};

export const resetPasswordWithToken = async ({
  token,
  password,
  confirm_password,
}) => {
  try {
    const res = await fetch(`${backendUrl}/auth/reset-password/confirm`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, password, confirm_password }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const error = new Error(data.details || data.message || "Reset failed");
      error.response = { data };
      error.status = res.status;
      throw error;
    }

    return data;
  } catch (error) {
    if (!error.response) {
      error.response = { data: { message: error.message } };
    }
    throw error;
  }
};

export const signupAdmin = async ({ payload, accessToken }) => {
  try {
    const res = await fetch(`${backendUrl}/auth/signup_admin`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken || ""}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const msg =
        data?.message || data?.details || "Failed to create owner account.";
      logger.warn(`signup_admin failed: ${msg}`);
      throw new Error(msg);
    }

    logger.info(`signup_admin success for owner ${payload?.email}`);
    return data;
  } catch (err) {
    logger.error(`signup_admin error: ${err.message}`);
    throw err;
  }
};

export const impersonateUser = async (userId) => {
  const accessToken = localStorage.getItem("access_token");
  const res = await fetch(`${backendUrl}/auth/impersonate`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ user_id: userId }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = json?.details || json?.message || "Impersonation failed";
    throw new Error(msg);
  }

  const d = json.data;

  // Save current admin session
  const sessionKeys = [
    "access_token",
    "refresh_token",
    "expires_at",
    "email",
    "is_owner",
    "is_admin",
    "role",
    "permission_preset",
    "is_voice",
    "outbound_calls_enabled",
    "transfer_campaigns_enabled",
  ];
  sessionKeys.forEach((key) => {
    const val = localStorage.getItem(key);
    if (val !== null) localStorage.setItem(`admin_${key}`, val);
  });

  // Set impersonated user session
  localStorage.setItem("access_token", d.access_token);
  localStorage.setItem("refresh_token", d.refresh_token);
  localStorage.setItem("expires_at", d.expires_at);
  localStorage.setItem("email", d.email);
  localStorage.setItem("is_owner", d.is_owner ? "true" : "false");
  localStorage.setItem("is_admin", d.is_admin ? "true" : "false");
  localStorage.setItem("role", d.role ?? "");
  localStorage.setItem("permission_preset", d.permission_preset ?? "");
  localStorage.setItem("is_voice", d.is_voice_notes_user ?? false);
  localStorage.setItem("has_scheduling", d.has_scheduling ? "true" : "false");
  localStorage.setItem("outbound_calls_enabled", d.outbound_calls_enabled ? "true" : "false");
  localStorage.setItem("transfer_campaigns_enabled", d.transfer_campaigns_enabled ? "true" : "false");

  // Set impersonation flags
  localStorage.setItem("impersonating", "true");
  localStorage.setItem("impersonating_email", d.email);

  window.location.href = "/";
};

export const exitImpersonation = () => {
  const sessionKeys = [
    "access_token",
    "refresh_token",
    "expires_at",
    "email",
    "is_owner",
    "is_admin",
    "role",
    "permission_preset",
    "is_voice",
  ];

  // Restore admin session
  sessionKeys.forEach((key) => {
    const val = localStorage.getItem(`admin_${key}`);
    if (val !== null) localStorage.setItem(key, val);
  });

  // Clean up admin backup keys and impersonation flags
  sessionKeys.forEach((key) => localStorage.removeItem(`admin_${key}`));
  localStorage.removeItem("impersonating");
  localStorage.removeItem("impersonating_email");

  window.location.href = "/admin/clinics";
};
